import React, { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import api from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Loader2, Search, Send, Users, Filter, CheckCircle2, AlertCircle, Calendar } from 'lucide-react';

const ESTADO_META = {
    activo: { label: 'Activo', cls: 'bg-emerald-100 text-emerald-700' },
    sin_escuela: { label: 'Sin escuela', cls: 'bg-amber-100 text-amber-700' },
    pendiente: { label: 'Pendiente', cls: 'bg-slate-100 text-slate-600' },
    plan_activo: { label: 'Plan activo', cls: 'bg-blue-100 text-blue-700' },
};

function fmtDate(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch { return '—'; }
}

export default function RecipientsSelectorDialog({ open, campaign, onClose, onSent }) {
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [search, setSearch] = useState('');
    const [filtros, setFiltros] = useState({
        estado: 'all',          // all | activo | sin_escuela | pendiente
        recibido: 'all',        // all | no | si
        desde: '',
        hasta: '',
    });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open || !campaign) return;
        setLoading(true);
        setSelected(new Set());
        api.get(`/admin/campaigns/${campaign.id}/recipients`)
            .then(({ data }) => setUsers(data.users || []))
            .catch(() => toast.error('No se pudieron cargar los usuarios'))
            .finally(() => setLoading(false));
    }, [open, campaign]);

    const filtered = useMemo(() => {
        return users.filter((u) => {
            if (search) {
                const s = search.toLowerCase();
                if (!`${u.nombre} ${u.email} ${u.escuela_nombre}`.toLowerCase().includes(s)) return false;
            }
            if (filtros.estado !== 'all' && u.estado !== filtros.estado) return false;
            if (filtros.recibido === 'no' && u.already_received) return false;
            if (filtros.recibido === 'si' && !u.already_received) return false;
            if (filtros.desde && u.created_at && u.created_at.slice(0, 10) < filtros.desde) return false;
            if (filtros.hasta && u.created_at && u.created_at.slice(0, 10) > filtros.hasta) return false;
            return true;
        });
    }, [users, search, filtros]);

    const toggleAll = (val) => {
        if (val) {
            setSelected(new Set(filtered.filter((u) => !u.already_received).map((u) => u.id)));
        } else {
            setSelected(new Set());
        }
    };
    const toggleOne = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };

    const handleSend = async () => {
        if (selected.size === 0) {
            toast.error('Selecciona al menos un destinatario');
            return;
        }
        const ok = window.confirm(
            `¿Confirmar envío de "${campaign.label}"?\n\n` +
            `→ Destinatarios seleccionados: ${selected.size}\n` +
            `→ Asunto: ${campaign.subject}\n\n` +
            `Esta acción NO se puede deshacer.`
        );
        if (!ok) return;
        setSending(true);
        try {
            const { data } = await api.post(`/admin/campaigns/${campaign.id}/send`, {
                user_ids: Array.from(selected),
            });
            const s = data.stats || {};
            toast.success(`Envío completado: ${s.sent}/${s.total_attempted} OK${s.errors ? ` · ${s.errors} error(es)` : ''}.`);
            onSent && onSent();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Error al enviar');
        } finally {
            setSending(false);
        }
    };

    const elegiblesEnVista = filtered.filter((u) => !u.already_received).length;
    const allInViewSelected = elegiblesEnVista > 0 &&
        filtered.filter((u) => !u.already_received).every((u) => selected.has(u.id));

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-5xl h-[90vh] flex flex-col" data-testid="recipients-selector-dialog">
                <DialogHeader>
                    <DialogTitle>Seleccionar destinatarios — {campaign?.label}</DialogTitle>
                    <DialogDescription>
                        Filtra y selecciona manualmente quiénes recibirán este correo. Los duplicados se evitan automáticamente.
                    </DialogDescription>
                </DialogHeader>

                {/* Filtros */}
                <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                            <Input
                                placeholder="Buscar por nombre, email o escuela…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-8 h-8 text-xs"
                                data-testid="recipients-search"
                            />
                        </div>
                        <select
                            value={filtros.estado}
                            onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
                            className="h-8 rounded-md border border-slate-200 px-2 text-xs"
                            data-testid="recipients-filter-estado"
                        >
                            <option value="all">Todos los estados</option>
                            <option value="activo">Con escuela vinculada</option>
                            <option value="sin_escuela">Sin escuela vinculada</option>
                            <option value="pendiente">Pendientes (sin perfil)</option>
                            <option value="plan_activo">Plan activo</option>
                        </select>
                        <select
                            value={filtros.recibido}
                            onChange={(e) => setFiltros({ ...filtros, recibido: e.target.value })}
                            className="h-8 rounded-md border border-slate-200 px-2 text-xs"
                            data-testid="recipients-filter-recibido"
                        >
                            <option value="all">Todos</option>
                            <option value="no">Aún NO recibieron</option>
                            <option value="si">Ya recibieron</option>
                        </select>
                        <div className="flex items-center gap-1 text-xs">
                            <Calendar className="h-3 w-3 text-slate-400" />
                            <Input
                                type="date"
                                value={filtros.desde}
                                onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })}
                                className="h-8 w-32 text-xs"
                                data-testid="recipients-filter-desde"
                            />
                            <span className="text-slate-400">a</span>
                            <Input
                                type="date"
                                value={filtros.hasta}
                                onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })}
                                className="h-8 w-32 text-xs"
                                data-testid="recipients-filter-hasta"
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-3 text-slate-600">
                            <span><Users className="h-3 w-3 inline" /> {filtered.length} en vista · {elegiblesEnVista} elegibles</span>
                            <span className="text-emerald-700"><CheckCircle2 className="h-3 w-3 inline" /> {selected.size} seleccionados</span>
                        </div>
                        <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => toggleAll(true)} className="h-7 text-xs" data-testid="recipients-select-all">
                                Seleccionar todos (vista)
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => toggleAll(false)} className="h-7 text-xs" data-testid="recipients-deselect-all">
                                Deseleccionar todos
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Lista */}
                <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg" data-testid="recipients-list">
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                        </div>
                    ) : (
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50 sticky top-0 z-10">
                                <tr className="text-left">
                                    <th className="px-2 py-2 w-8">
                                        <Checkbox
                                            checked={allInViewSelected}
                                            onCheckedChange={(v) => toggleAll(!!v)}
                                            data-testid="recipients-header-checkbox"
                                        />
                                    </th>
                                    <th className="px-2 py-2 font-semibold text-slate-600">Docente</th>
                                    <th className="px-2 py-2 font-semibold text-slate-600">Escuela</th>
                                    <th className="px-2 py-2 font-semibold text-slate-600">Estado</th>
                                    <th className="px-2 py-2 font-semibold text-slate-600">Registro</th>
                                    <th className="px-2 py-2 font-semibold text-slate-600">Última actividad</th>
                                    <th className="px-2 py-2 font-semibold text-slate-600">Esta campaña</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((u) => {
                                    const meta = ESTADO_META[u.estado] || ESTADO_META.pendiente;
                                    const isSelected = selected.has(u.id);
                                    return (
                                        <tr
                                            key={u.id}
                                            className={cn(
                                                'border-t border-slate-100',
                                                u.already_received && 'opacity-50 bg-slate-50',
                                                isSelected && 'bg-blue-50/50'
                                            )}
                                            data-testid={`recipient-row-${u.id}`}
                                        >
                                            <td className="px-2 py-2">
                                                <Checkbox
                                                    checked={isSelected}
                                                    disabled={u.already_received}
                                                    onCheckedChange={() => toggleOne(u.id)}
                                                    data-testid={`recipient-checkbox-${u.id}`}
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                <p className="font-medium text-slate-900">{u.nombre || u.email}</p>
                                                <p className="text-[10px] text-slate-500">{u.email}</p>
                                            </td>
                                            <td className="px-2 py-2 text-slate-700 truncate max-w-[180px]">
                                                {u.escuela_nombre || <span className="text-slate-400 italic">sin vincular</span>}
                                            </td>
                                            <td className="px-2 py-2">
                                                <Badge className={cn('text-[9px]', meta.cls)}>{meta.label}</Badge>
                                            </td>
                                            <td className="px-2 py-2 text-slate-500 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                                            <td className="px-2 py-2 text-slate-500 whitespace-nowrap">{fmtDate(u.last_activity)}</td>
                                            <td className="px-2 py-2">
                                                {u.already_received ? (
                                                    <Badge className="bg-emerald-50 text-emerald-700 text-[9px]">
                                                        <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Recibido
                                                    </Badge>
                                                ) : (
                                                    <span className="text-[9px] text-slate-400">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="text-center py-8 text-slate-400 text-xs">
                                            Sin resultados con los filtros aplicados.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                <DialogFooter className="border-t pt-3">
                    <div className="flex items-center justify-between w-full gap-2 flex-wrap">
                        <div className="text-xs text-slate-600">
                            {selected.size > 0 ? (
                                <span className="font-medium text-emerald-700">
                                    <Filter className="h-3 w-3 inline" /> Se enviará a <strong>{selected.size}</strong> docente(s) seleccionado(s)
                                </span>
                            ) : (
                                <span className="text-amber-700">
                                    <AlertCircle className="h-3 w-3 inline" /> Selecciona al menos un destinatario
                                </span>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={onClose}>Cancelar</Button>
                            <Button
                                onClick={handleSend}
                                disabled={selected.size === 0 || sending}
                                className="gap-1.5 bg-slate-900 hover:bg-slate-800"
                                data-testid="recipients-send-btn"
                            >
                                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                                Enviar a {selected.size} seleccionado(s)
                            </Button>
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
